import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Panel} from 'primeng/panel';
import {TableModule} from 'primeng/table';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {Divider} from 'primeng/divider';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService, MessageService} from 'primeng/api';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import {Road} from '../../shared/models/common/road.model';
import {Store} from '@ngrx/store';
import * as action from '../store/common.actions';
import {createRoad, deleteRoad, findRoads, updateRoad} from '../store/common.actions';
import {CommonEffects} from '../store/common.effects';
import {selectRoadData, selectRoadTotal} from '../store/common.selector';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {selectIsLoadingAction} from '../../shared/store/global';
import {Actions, ofType} from '@ngrx/effects';
import {BaseListComponent} from '../../shared/components/base-list.component';
import { AssetHistoryDialogComponent } from '../../shared/components/asset-history-dialog/asset-history-dialog.component';
import { ColumnFilters } from '../../shared/models/ui/table-column.model';

@Component({
  selector: 'roads-list',
  templateUrl: './roads-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Divider,
    ConfirmDialogModule,
    DialogModule,
    ToastModule,
    AssetHistoryDialogComponent
  ],
  providers: [ConfirmationService, MessageService, CommonEffects],
  styleUrls: ['./roads-list.component.scss']
})
export class RoadsListComponent extends BaseListComponent implements OnInit, OnDestroy {

  private messageService = inject(MessageService);
  private store$ = inject(Store<{ common: any }>)
  private actions$ = inject(Actions);

  roads = toSignal(this.store$.select(selectRoadData), {initialValue: []});
  totalRecords = toSignal(this.store$.select(selectRoadTotal), {initialValue: 0});
  loading = toSignal(this.store$.select(selectIsLoadingAction('[Common] Find Roads')), {initialValue: false});

  
  readonly auditResourceName = 'Road';
  historyVisible = false;
  historyRecordId: number | string | null = null;
  historyRecordLabel = '';

  selectedRoads = signal<Road[]>([]);

  // Dialog state
  dialogVisible = signal(false);
  roadName = signal('');
  roadCode = signal('');
  myinfraId = signal('');
  marrisId = signal('');
  savingRoad = signal(false);
  isEditMode = signal(false);
  editingRoadId = signal<number | null>(null);

  deleteButtonLabel = computed(() => {
    return this.selectedRoads().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  override ngOnInit(): void {
    super.ngOnInit();

    // Handle UI state after successful operations
    this.actions$.pipe(
      ofType(action.createRoadSuccess, action.updateRoadSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingRoad.set(false);
      this.closeDialog();
      // Refresh current view
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    });

    this.actions$.pipe(
      ofType(action.deleteRoadSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.selectedRoads.set([]);
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    });

    // Handle errors (reset saving state)
    this.actions$.pipe(
      ofType(action.createRoadError, action.updateRoadError, action.deleteRoadError),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingRoad.set(false);
    });
  }

  // No need for ngOnDestroy override as super handles destroy$
  // But we have local subscriptions using it? Yes, but super destroys it.
  // Wait, super.ngOnDestroy destroys destroy$.
  // If we don't override, super.ngOnDestroy is called.
  // If we override, we must call super.ngOnDestroy().

  override ngOnDestroy(): void {
     super.ngOnDestroy();
  }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    const keywords = filters['keywords'] || '';
    this.store$.dispatch(findRoads({
      page,
      size,
      keywords
    }));
  }

  addNew(): void {
    this.roadName.set('');
    this.roadCode.set('');
    this.myinfraId.set('');
    this.marrisId.set('');
    this.isEditMode.set(false);
    this.editingRoadId.set(null);
    this.dialogVisible.set(true);
  }

  closeDialog(): void {
    this.dialogVisible.set(false);
    this.roadName.set('');
    this.roadCode.set('');
    this.myinfraId.set('');
    this.marrisId.set('');
    this.savingRoad.set(false);
    this.isEditMode.set(false);
    this.editingRoadId.set(null);
  }

  saveRoad(): void {
    if (!this.roadName().trim() || !this.roadCode().trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Road name and road code are required'
      });
      return;
    }

    this.savingRoad.set(true);

    const roadData: Road = {
      id: this.editingRoadId() || 0,
      road_name: this.roadName().trim(),
      road_code: this.roadCode().trim(),
      myinfra_id: this.myinfraId().trim() || undefined,
      marris_id: this.marrisId().trim() || undefined,
    };

    if (this.isEditMode() && this.editingRoadId()) {
      // Update existing road
      this.store$.dispatch(updateRoad({id: this.editingRoadId()!, road: roadData}));
    } else {
      // Create new road
      this.store$.dispatch(createRoad({road: roadData}));
    }
  }

  editRoad(): void {
    if (this.selectedRoads().length === 1) {
      const roadToEdit = this.selectedRoads()[0];
      this.roadName.set(roadToEdit.road_name ? roadToEdit.road_name : '');
      this.roadCode.set(roadToEdit.road_code ? roadToEdit.road_code : '');
      this.myinfraId.set(roadToEdit.myinfra_id ? roadToEdit.myinfra_id : '');
      this.marrisId.set(roadToEdit.marris_id ? roadToEdit.marris_id : '');
      this.isEditMode.set(true);
      this.editingRoadId.set(roadToEdit.id);
      this.dialogVisible.set(true);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one road to edit'
      });
    }
  }

  deleteRoad(): void {
    if (this.selectedRoads().length === 0) {
      return;
    }

    const roadsToDelete = [...this.selectedRoads()];
    const count = roadsToDelete.length;

    let message: string;
    if (count === 1) {
      message = `Are you sure you want to delete "${roadsToDelete[0].road_name}"?`;
    } else {
      const maxDisplay = 10;
      const codesToShow = roadsToDelete.slice(0, maxDisplay);
      const codesList = codesToShow.map((r, index) => `${index + 1}. ${r.road_code}`).join('<br>');

      let fullMessage = `Are you sure you want to delete ${count} roads?<br><br><strong>Codes to be deleted:</strong><br>${codesList}`;

      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }

      message = fullMessage;
    }

    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        // Delete roads one by one
        roadsToDelete.forEach(road => {
          this.store$.dispatch(deleteRoad({id: road.id}));
        });
      }
    });
  }

  refreshRoads(): void {
    // Reset to default state
    this.selectedRoads.set([]);
    
    // Call base class clearFilter to reset pagination and keywords
    this.clearFilter();

    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'Road list has been refreshed'
    });
  }

  showHistory(): void {
    const selected = this.selectedRoads();
    if (selected.length !== 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'History',
        detail: 'Select one record to view history'
      });
      return;
    }
    const item = selected[0] as any;
    this.historyRecordId = item?.id ?? null;
    this.historyRecordLabel = item?.road_name || item?.name || String(this.historyRecordId ?? '');
    this.historyVisible = true;
  }
}
